import { astraTwMerge } from '~/astra'
import { selectStatusValues } from '~/store/slices/surgeonSchedulerFilterSlice'
import { selectPendingLocationSchedulesForCell } from '~/store/slices/surgeonSchedulerSlice'
import { useStore } from '~/store/store'
import { Location, Practitioner, usePractitionerScheduleLocationsForCell } from '~/storm'

import { type PractitionerLocationActivity } from '../../../PractitionerActivityDnd'
import { CellErrorHoverCard, EditInfoFooter, getDateBreadcrumb, getEditInfoText, PractitionerCellErrors } from '../../../shared'
import { ActivityTag } from './ActivityTag'

export const useCellLocationSchedules = (practitionerId: Practitioner['id'], date: string) => {
    const databaseLocationSchedules = usePractitionerScheduleLocationsForCell(practitionerId, date)
    const pendingLocationSchedules = useStore(selectPendingLocationSchedulesForCell(practitionerId, date))
    const previewLocationSchedules = useStore(state => state.surgeonScheduler.recurringAssignmentApplyPreviewData.locations).filter(
        location => location.date === date && location.practitionerId === practitionerId
    )

    const allRoomCodes = [
        ...databaseLocationSchedules.map(a => a.location?.room_code),
        ...pendingLocationSchedules.map(a => a.room_code),
        ...previewLocationSchedules.map(a => a.location.room_code),
    ]
        .filter(Boolean)
        .toSorted()

    return {
        databaseLocationSchedules,
        pendingLocationSchedules,
        previewLocationSchedules,
        allRoomCodes,
    }
}

export type CellLocationSchedules = ReturnType<typeof useCellLocationSchedules>

type LocationActivityTagProps = {
    roomCode: Location['room_code']
    dragItem?: PractitionerLocationActivity
    label: string
    editInfo?: string
    isGrayedOut?: boolean
    isLoading?: boolean
    cellErrors: PractitionerCellErrors | undefined
}

const LocationActivityTag = ({ roomCode, dragItem, label, editInfo, isGrayedOut = false, isLoading = false, cellErrors }: LocationActivityTagProps) => {
    const errors = cellErrors?.errorsByLocation.get(roomCode) ?? []
    if (cellErrors && errors.length > 0) {
        return (
            <CellErrorHoverCard
                breadcrumbs={[getDateBreadcrumb(cellErrors.cell.scheduled_date), { label: cellErrors.practitioner?.short_name ?? '' }]}
                triggerInfo={{ shortName: label }}
                errors={cellErrors.errorsByLocation.get(roomCode) ?? []}
                footer={editInfo && <EditInfoFooter editInfo={editInfo} />}
            >
                <ActivityTag dragItem={dragItem} colorType="room_conflict" isGrayedOut={isGrayedOut} label={label} isLoading={isLoading} />
            </CellErrorHoverCard>
        )
    }

    const tooltip = [label, editInfo].filter(Boolean).join('\n')
    return <ActivityTag dragItem={dragItem} colorType="room" isGrayedOut={isGrayedOut} tooltip={tooltip} label={label} isLoading={isLoading} />
}

type Props = {
    cellErrors: PractitionerCellErrors | undefined
    toBeDropped: Location[] | undefined
    locationSchedules: CellLocationSchedules
    showPreview: boolean
}

export const Locations = ({ toBeDropped, cellErrors, locationSchedules, showPreview }: Props) => {
    const filteredStatuses = useStore(selectStatusValues)
    const { databaseLocationSchedules, pendingLocationSchedules, previewLocationSchedules } = locationSchedules

    const allLocationsLength = databaseLocationSchedules.length + pendingLocationSchedules.length + (showPreview ? previewLocationSchedules.length : 0)

    return (
        <div className={astraTwMerge('cell two-children flex items-stretch gap-x-0.5', allLocationsLength > 3 ? '-space-x-5' : '')}>
            {databaseLocationSchedules.map(cellLocation => {
                const roomCode = cellLocation.location?.room_code ?? ''

                return (
                    <LocationActivityTag
                        roomCode={roomCode}
                        dragItem={cellLocation}
                        key={cellLocation.location?.uuid}
                        isGrayedOut={filteredStatuses.length > 0}
                        editInfo={getEditInfoText(cellLocation)}
                        label={roomCode}
                        cellErrors={cellErrors}
                    />
                )
            })}
            {showPreview &&
                previewLocationSchedules.map(location => {
                    const roomCode = location.location.room_code

                    return (
                        <LocationActivityTag
                            key={location.location.uuid}
                            roomCode={roomCode}
                            isGrayedOut={filteredStatuses.length > 0}
                            editInfo={getEditInfoText(location)}
                            label={roomCode}
                            cellErrors={cellErrors}
                        />
                    )
                })}
            {pendingLocationSchedules.map(location => (
                <LocationActivityTag key={location.room_code} roomCode={location.room_code} label={location.room_code} isLoading cellErrors={cellErrors} />
            ))}

            {toBeDropped?.map(location => (
                <LocationActivityTag key={location.room_code} roomCode={location.room_code} label={location.room_code} cellErrors={cellErrors} />
            ))}
        </div>
    )
}
